// @ts-strict-ignore
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useParams,
  useLocation,
  NavLink,
  useNavigate,
} from 'react-router-dom';
import {
  Box, Text, useSnackBar,
} from '@usealloy/component-library';
import type { FieldValues } from 'react-hook-form';
import {
  useForm,
  FormProvider,
} from 'react-hook-form';

import { ajaxFetch } from '~/helpers/api';
import log from '~/helpers/logging';
import { routes } from '~/helpers/routes';
import FormContainer from '../FormContainer';
import type { TooltipContentType } from '../FormContainer/types';
import { InputFieldType } from '../FormContainer/types';
import { StyledLink } from '../AuthContainerView/styles';
import PasswordValidationList from './PasswordValidationList';
import { ResetPasswordViewMode } from '~/dashboard/constants';

interface IFormFields {
  password: string;
  password_confirmation: string;
}

const formDefaultValues = {
  password: '',
  password_confirmation: '',
};


const ResetPasswordView = ({
  mode = ResetPasswordViewMode.RESETPW,
}: { mode: ResetPasswordViewMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const addSnackBar = useSnackBar();
  const { tokenUid, token } = useParams();

  const methods = useForm<IFormFields>({ defaultValues: formDefaultValues, mode: 'all' });

  const [validChecks, setValidChecks] = useState(false);
  const passwordInputValue = methods.watch('password');
  const renderPasswordValidationList = useCallback(() => (
    <PasswordValidationList inputValue={passwordInputValue} setValidChecks={setValidChecks} />
  ), [passwordInputValue]);

  useEffect(() => {
    methods.setFocus('password');
  }, [methods, passwordInputValue]);

  const tokenVerifyEndpointUrl = useMemo(() => (
    mode === ResetPasswordViewMode.SETPW
      ? routes.authentication.agentInvitation(`${tokenUid}/${token}`)
      : routes.authentication.resetPassword.withToken(`${tokenUid}/${token}`)
  ), [mode, token, tokenUid]);

  const nextUrl = useMemo(() => (location?.state?.nextUrl || '/'), [location]);

  const verifyToken = useCallback(async () => {
    try {
      const response = await ajaxFetch(tokenVerifyEndpointUrl, { enhancedErrorResponse: true });

      if (response?.result !== 'success') {
        addSnackBar({
          title: 'Something went wrong while verifying the link. Please try again or contact support.',
          type: 'danger',
        });
      }
    } catch (error) {
      navigate(routes.v3.dashboard.authentication.login, {
        replace: true,
        state: {
          message: {
            text: error.message || 'Something went wrong while verifying the link. Please try again or contact support.',
            type: 'danger',
          },
        },
      });
      log('error', `GET ${tokenVerifyEndpointUrl} exception`, {}, error);
    }
  }, [tokenVerifyEndpointUrl, addSnackBar, navigate]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const fields = [
    {
      type: InputFieldType.PASSWORD,
      label: 'New Password',
      name: 'password',
      error: 'New password required',
      required: true,
      hasTooltip: true,
      toolTipContent: {
        type: 'method' as TooltipContentType,
        method: renderPasswordValidationList,
        text: null,
      },
      validChecks,
    },
    {
      type: InputFieldType.PASSWORD,
      label: 'Retype Password',
      name: 'password_confirmation',
      error: 'Password should be same as above',
      required: true,
    },
  ];

  const onResetPassSubmit = async (formData: FieldValues) => {
    const genericErrorMessage = 'Something went wrong while setting password.';
    try {
      const response = await ajaxFetch(tokenVerifyEndpointUrl,
        {
          method: 'POST',
          body: JSON.stringify({ ...formData, next_url: nextUrl }),
          camelCase: true,
          enhancedErrorResponse: true,
        },
      );

      if (response?.result === 'success') {
        const successMsg = mode === ResetPasswordViewMode.SETPW
          ? 'Your password was successfully configured. Please login.'
          : 'Your password was successfully reset. Please login again.';

        navigate(routes.v3.dashboard.authentication.login,
          {
            replace: true,
            state: {
              message: {
                text: successMsg,
                type: 'success',
              },
            },
          });
      }
    } catch (err) {
      addSnackBar({
        title: err.message || genericErrorMessage,
        type: 'danger',
      });
      log('error', `GET ${tokenVerifyEndpointUrl} exception`, {}, err);
    }
  };

  const viewModeString = useMemo(() => (mode === ResetPasswordViewMode.SETPW
    ? 'Set' : 'Reset'), [mode]);

  return (
    <Box direction="column" gap={4} alignItems="stretch" style={{ width: '440px' }}>
      <Text as="h1" size={7} style={{ textAlign: 'center' }}>{viewModeString} Password</Text>
      <FormProvider {...methods}>
        <FormContainer
          fields={fields}
          onSubmit={onResetPassSubmit}
          onSubmitActionText={viewModeString}
          validChecks={validChecks}
        />
      </FormProvider>

      <NavLink to={routes.v3.dashboard.authentication.login}>
        <StyledLink forwardedAs="span">
          Return to sign in
        </StyledLink>
      </NavLink>
    </Box>
  );
};

export { PasswordValidationList };
export default ResetPasswordView;
